/**
 * ECharts Option Builder — Line / Area chart.
 */
import type { EChartsOption } from 'echarts';
import { type ChartTheme, buildBaseOption, legendConfig, xAxisConfig, yAxisConfig } from '../theme';
import { formatChartValue } from '../../../../utils/formatters';
import type { ChartData } from '../../../../api/types';
import { autoRotate, estimateYAxisNameGap } from './utils';
import { extractSeriesData } from './seriesData';

export function buildLineChart(
  data: ChartData,
  theme: ChartTheme,
  animated: boolean,
  isArea: boolean,
  containerWidth: number = 400,
): EChartsOption {
  const sd = extractSeriesData(data);

  // Hide symbols on dense time series (> 60 data points)
  const isDense = sd.categories.length > 60;

  const series = sd.seriesNames.map((name) => ({
    type: 'line' as const,
    sampling: 'lttb' as const, // downsample dense data for performance
    progressive: 2000,
    progressiveThreshold: 5000,
    name,
    data: sd.seriesValues.get(name)!,
    smooth: false,
    symbol: 'circle' as const,
    symbolSize: 3,
    showSymbol: !isDense,
    lineStyle: { width: 2 },
    itemStyle: { opacity: 0.9, borderWidth: 2 },
    emphasis: { focus: 'none' as const, scale: false },
    ...(isArea ? { areaStyle: { opacity: 0.12 } } : {}),
    ...(isDense ? { sampling: 'lttb' as const } : {}),
  }));

  // Show y-axis label when single series (label already contains friendly name)
  const yAxisName = sd.seriesNames.length === 1 ? sd.seriesNames[0] : undefined;
  const nameGap = estimateYAxisNameGap(sd.seriesValues);

  // X-axis name: use the x column name so it appears when axis label is enabled
  const meta = data.meta;
  const xCol = meta?.x ?? data.columns[0].name;

  return {
    ...buildBaseOption(theme, animated),
    legend: { ...legendConfig(theme), show: sd.seriesNames.length > 1 },
    xAxis: {
      ...xAxisConfig(theme),
      name: xCol,
      data: sd.categories,
      axisLabel: { ...xAxisConfig(theme).axisLabel, rotate: autoRotate(sd.categories, containerWidth) },
    },
    yAxis: {
      ...yAxisConfig(theme),
      name: yAxisName,
      nameGap,
      axisLabel: { ...yAxisConfig(theme).axisLabel, formatter: (v: number) => formatChartValue(v) },
    },
    series,
  };
}